import { makeStyles } from 'tss-react/mui'
import { alpha, Theme } from '@mui/material/styles'
import { keyframes } from '@emotion/react'

// Coral sliver sweeping the track while a resume_poc_run submit is in flight.
const indeterminateSweep = keyframes({
  '0%': { transform: 'translateX(-100%)' },
  '100%': { transform: 'translateX(350%)' },
})

// A light band travelling across the determinate fill while a run is active, so
// the bar reads as "working" between width jumps.
const fillShimmer = keyframes({
  '0%': { backgroundPosition: '200% 0' },
  '100%': { backgroundPosition: '-200% 0' },
})

export const useStyles = makeStyles()((theme: Theme) => {
  // Done/complete reads as "success" (green); the active step reads as
  // "in progress"/"warning" (amber) — matching the app's stat-card semantics.
  const success = theme.palette.syncStatus.success
  const amber = theme.palette.labraChat.warningText
  const amberBg = theme.palette.labraChat.warningBg
  return {
    card: {
      boxSizing: 'border-box',
      width: '100%',
      backgroundColor: theme.palette.text.white,
      border: `1px solid ${theme.palette.labraChat.borderLight}`,
      borderRadius: 14,
      overflow: 'hidden',
      boxShadow: '0px 1px 3px 0px rgba(11,11,16,0.05)',
      fontFamily: 'Inter, sans-serif',
    },
    body: {
      padding: '18px 20px',
    },
    header: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 16,
    },
    title: {
      fontSize: 15,
      fontWeight: 700,
      color: theme.palette.labraText.tertiary,
    },
    subtitle: {
      fontSize: 12,
      fontWeight: 500,
      color: theme.palette.labraText.description,
      marginTop: 2,
    },
    pctGroup: {
      textAlign: 'right',
      flex: 'none',
    },
    pct: {
      fontSize: 24,
      fontWeight: 700,
      color: success,
      lineHeight: 1,
    },
    pctCaption: {
      fontSize: 9.5,
      fontWeight: 600,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      color: theme.palette.labraText.secondary,
      marginTop: 3,
    },
    track: {
      marginTop: 14,
      height: 8,
      width: '100%',
      backgroundColor: theme.palette.labraChat.hoverBgAlt,
      borderRadius: 999,
      overflow: 'hidden',
    },
    fill: {
      height: '100%',
      backgroundColor: success,
      borderRadius: 999,
      transition: 'width 360ms cubic-bezier(0.2,0.8,0.2,1)',
    },
    // While a run is in flight, overlay a travelling light band on the solid
    // fill. Disabled for users who prefer reduced motion.
    fillActive: {
      backgroundImage: `linear-gradient(90deg, ${alpha(theme.palette.text.white, 0)} 0%, ${alpha(theme.palette.text.white, 0.45)} 50%, ${alpha(theme.palette.text.white, 0)} 100%)`,
      backgroundSize: '200% 100%',
      backgroundRepeat: 'no-repeat',
      animation: `${fillShimmer} 1.6s linear infinite`,
      '@media (prefers-reduced-motion: reduce)': {
        animation: 'none',
        backgroundImage: 'none',
      },
    },
    // Indeterminate bar for resume_poc_run (no stage replay) — a sliver sweeping
    // the track while we wait for the submit to finish.
    fillIndeterminate: {
      width: '40%',
      animation: `${indeterminateSweep} 1.2s ease-in-out infinite`,
    },
    completedLabel: {
      fontSize: 12.5,
      fontWeight: 500,
      color: theme.palette.labraText.description,
      marginTop: 9,
    },
    divider: {
      height: 1,
      backgroundColor: theme.palette.labraChat.borderLight,
      margin: '16px 0 4px',
    },
    // --- Stepper -----------------------------------------------------------
    stepRow: {
      position: 'relative',
      display: 'flex',
      alignItems: 'flex-start',
      gap: 13,
      minHeight: 46,
      padding: '0 6px',
      borderRadius: 9,
    },
    stepRowCurrent: {
      backgroundColor: alpha(amber, 0.05),
    },
    railColumn: {
      position: 'relative',
      width: 28,
      flex: 'none',
      alignSelf: 'stretch',
      display: 'flex',
      justifyContent: 'center',
    },
    connector: {
      position: 'absolute',
      top: 28,
      bottom: -2,
      left: 13,
      width: 2,
    },
    connectorDone: {
      backgroundColor: success,
    },
    connectorPending: {
      backgroundColor: theme.palette.labraChat.borderLight,
    },
    circle: {
      position: 'relative',
      zIndex: 1,
      marginTop: 9,
      boxSizing: 'border-box',
      width: 28,
      height: 28,
      // Keep it perfectly round: without this the circle compresses
      // horizontally into an oval when the chat panel is narrow.
      flexShrink: 0,
      borderRadius: 999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: '2px solid transparent',
    },
    circlePending: {
      backgroundColor: theme.palette.labraChat.hoverBgAlt,
    },
    circleDone: {
      backgroundColor: success,
    },
    circleFailed: {
      backgroundColor: amberBg,
      border: `2px solid ${amber}`,
    },
    circleCurrent: {
      backgroundColor: theme.palette.text.white,
      border: `2px solid ${amber}`,
      boxShadow: `0 0 0 4px ${alpha(amber, 0.18)}`,
    },
    circleNum: {
      fontSize: 12.5,
      fontWeight: 600,
      color: theme.palette.labraText.secondary,
    },
    circleNumCurrent: {
      color: amber,
    },
    checkIcon: {
      color: theme.palette.text.white,
      fontSize: 13,
    },
    failedIcon: {
      color: amber,
      fontSize: 13,
    },
    stepTextCol: {
      flex: 1,
      minWidth: 0,
      paddingTop: 13,
      paddingBottom: 7,
    },
    stepName: {
      fontSize: 14,
      fontWeight: 500,
      color: theme.palette.labraText.secondary,
    },
    stepNameActive: {
      fontWeight: 600,
      color: theme.palette.labraText.tertiary,
    },
    stepNameDone: {
      color: theme.palette.labraText.tertiary,
    },
    stepDetail: {
      fontSize: 12,
      fontWeight: 400,
      color: theme.palette.labraText.description,
      marginTop: 2,
    },
    // Right-aligned status affordances on each step row.
    inProgressPill: {
      alignSelf: 'center',
      flex: 'none',
      padding: '4px 11px',
      borderRadius: 999,
      backgroundColor: amberBg,
      color: amber,
      fontSize: 11,
      fontWeight: 600,
      whiteSpace: 'nowrap',
    },
    doneLabel: {
      alignSelf: 'center',
      flex: 'none',
      fontSize: 11.5,
      fontWeight: 600,
      color: success,
      whiteSpace: 'nowrap',
    },
    failedLabel: {
      alignSelf: 'center',
      flex: 'none',
      fontSize: 11.5,
      fontWeight: 600,
      color: amber,
      whiteSpace: 'nowrap',
    },
    submittingNote: {
      marginTop: 14,
      fontSize: 13,
      fontWeight: 500,
      color: theme.palette.labraText.description,
    },
  }
})
